import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiLock, FiMail, FiEye, FiEyeOff } from "react-icons/fi";
import { motion } from "framer-motion";
import ThemeToggle from "../components/ThemeToggle";
import { useTheme } from "../context/ThemeContext";
import api from "../services/api";

const policyContent = `
<h2>About RMC2 KAAGAPAY Emergency Access</h2>
<br/>
<p>RMC2 KAAGAPAY Emergency Access is a ONE-TIME USE managed care program of RMC2 Consultancy Corporation created to help individuals and families prepare for unexpected medical emergencies.</p>
<br/>
<p>Through the KAAGAPAY Emergency Card, members receive one-year emergency medical assistance that helps cover reasonable and customary emergency expenses in AMAPHIL affiliated hospitals nationwide.</p>
<br/>
<p>The program is designed to provide timely financial support when sudden illness or injury requires immediate medical attention.</p>
<br/>
  <p>Beyond emergency support, KAAGAPAY also offers members an opportunity to earn incentives by sharing the program with others. Through a structured referral system, members can help extend protection to more families while building an additional source of income.</p>
`;

const container = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, staggerChildren: 0.12 },
  },
};

const item = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};

const Login = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canResendVerification, setCanResendVerification] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendNotice, setResendNotice] = useState("");
  const { isDark } = useTheme();
  const logoSrc = isDark ? "/logo512-white.png" : "/logo512.png";
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    contactNumber: "",
    message: "",
  });
  const [contactError, setContactError] = useState("");
  const [contactSuccess, setContactSuccess] = useState("");
  const [isContactSubmitting, setIsContactSubmitting] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("rmc2.access_token");
    if (!token) {
      return;
    }

    api
      .post(
        "/validate-token",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then(() => {
        navigate("/dashboard", { replace: true });
      })
      .catch(() => {
        localStorage.removeItem("rmc2.access_token");
        localStorage.removeItem("rmc2.scope");
      });
  }, [navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setIsSubmitting(true);
    setCanResendVerification(false);
    setResendNotice("");

    try {
      const response = await api.post("/login", {
        username,
        password,
        rememberMe,
      });

      const data = response.data || {};

      if (!data.access_token) {
        throw new Error("Login failed. Missing access token.");
      }

      localStorage.setItem("rmc2.access_token", data.access_token);
      if (data.refresh_token) {
        localStorage.setItem("rmc2.refresh_token", data.refresh_token);
      }
      if (data.token_type) {
        localStorage.setItem("rmc2.token_type", data.token_type);
      }
      if (data.expires_in) {
        localStorage.setItem("rmc2.token_expires_at", String(data.expires_in));
      }
      if (data.scope) {
        localStorage.setItem("rmc2.scope", data.scope);
      } else {
        api
          .get("/me", {
            headers: {
              Authorization: `Bearer ${data.access_token}`,
            },
          })
          .then((meResponse) => {
            const scope = meResponse.data?.data?.scope;
            if (scope) {
              localStorage.setItem("rmc2.scope", scope);
            }
          })
          .catch(() => {});
      }

      window.location.replace("/dashboard");
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error_description ||
        error?.message ||
        "Login failed. Please try again.";
      setErrorMessage(apiMessage);
      if (String(apiMessage).toLowerCase().includes("email not verified")) {
        setCanResendVerification(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    if (!username) {
      setResendNotice("Please enter your username first.");
      return;
    }
    setIsResending(true);
    setResendNotice("");
    try {
      const response = await api.post("/verify-email/resend", { username });
      if (response.data?.status === "success") {
        setResendNotice(response.data?.message || "Verification email sent.");
        return;
      }
      setResendNotice("Unable to resend verification email.");
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to resend verification email.";
      setResendNotice(message);
    } finally {
      setIsResending(false);
    }
  };

  const countWords = (value) =>
    value
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;

  const handleContactSubmit = async () => {
    setContactError("");
    setContactSuccess("");
    const { name, email, contactNumber, message } = contactForm;
    if (!name.trim() || !email.trim() || !contactNumber.trim() || !message.trim()) {
      setContactError("All fields are required.");
      return;
    }
    const wordCount = countWords(message);
    if (wordCount < 5) {
      setContactError("Message must be at least 5 words.");
      return;
    }
    if (wordCount > 3000) {
      setContactError("Message must not exceed 3000 words.");
      return;
    }
    setIsContactSubmitting(true);
    try {
      const response = await api.post("/contact-us", {
        name: name.trim(),
        email: email.trim(),
        contact_number: contactNumber.trim(),
        message: message.trim(),
      });
      if (response.data?.status !== "success") {
        setContactError(response.data?.message || "Failed to send message.");
        return;
      }
      setContactSuccess(response.data?.message || "Message sent successfully.");
      setContactForm({ name: "", email: "", contactNumber: "", message: "" });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to send message.";
      setContactError(message);
    } finally {
      setIsContactSubmitting(false);
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="show"
      variants={container}
      className="relative min-h-screen overflow-hidden bg-[linear-gradient(140deg,var(--shell-start)_0%,var(--shell-end)_50%,var(--shell-glow-2)_100%)]"
    >
      {/* <div className="pointer-events-none absolute left-6 top-6 h-24 w-24 rounded-3xl border border-white/70 bg-white/70 shadow-lg" />
      <div className="pointer-events-none absolute right-6 bottom-6 h-28 w-28 rounded-3xl border border-white/70 bg-white/70 shadow-lg" /> */}

      <div className="relative z-10 mx-auto flex min-h-screen max-w-[1200px] flex-col px-6 pb-16 pt-10">
        <motion.header
          variants={item}
          className="flex items-center justify-between gap-6 text-slate-900"
        >
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-lg">
              <img src={logoSrc} alt="RMC2" className="h-8 w-8" />
            </div>
            <div>
              <p className="text-2xl font-semibold">RMC2 KAAGAPAY Emergency Access</p>
              <p className="text-sm text-slate-500">Kaagapay Mo sa Oras ng Emergency</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-500 lg:block">
              Emergency Care Program
            </div>
            <ThemeToggle />
          </div>
        </motion.header>

        <div className="mt-10 grid flex-1 gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <motion.div
            variants={item}
            className="rounded-[28px] border border-white/60 bg-white/90 p-8 text-left shadow-2xl shadow-slate-200/60"
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[var(--brand-blue-2)]">
                About
              </p>
              <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-[var(--brand-blue)]">
                Coverage
              </span>
            </div>
            <h2 className="mt-3 text-3xl font-semibold text-slate-900">
              Emergency Access Overview
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              A quick overview of the KAAGAPAY program and member benefits.
            </p>
            <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-700 shadow-inner">
              <div
                className="prose max-h-[500px] max-w-none overflow-y-auto prose-headings:text-slate-900 prose-p:text-slate-700 prose-strong:text-slate-900"
                dangerouslySetInnerHTML={{ __html: policyContent }}
              />
            </div>
          </motion.div>

          <motion.div
            variants={item}
            className="rounded-[28px] border border-white/60 bg-white/95 p-9 shadow-2xl shadow-slate-200/60"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
              Log in
            </p>
            <h1 className="mt-4 text-3xl font-semibold text-slate-900">Welcome back</h1>
            <p className="mt-3 text-sm text-slate-500">
              Enter your credentials to continue managing your account.
            </p>

            <form onSubmit={handleSubmit} className="mt-7 space-y-6">
              <div>
                <label className="text-sm font-semibold text-slate-600">Username</label>
                <div className="relative mt-2">
                  <FiMail className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    placeholder="Username"
                    autoComplete="username"
                    className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-600">Password</label>
                <div className="relative mt-2">
                  <FiLock className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Password"
                    autoComplete="current-password"
                    className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-12 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-600"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm">
                  <label className="flex items-center gap-2 text-slate-600">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(event) => setRememberMe(event.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-200"
                    />
                    Remember me
                  </label>
                  <Link
                    to="/forgot-password"
                    className="font-semibold text-[var(--brand-blue-2)] transition hover:text-[var(--brand-blue)]"
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>

              {errorMessage ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  <p>{errorMessage}</p>
                  {canResendVerification && (
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={handleResendVerification}
                        disabled={isResending}
                        className="rounded-full border border-rose-200 bg-white px-3 py-1 text-xs font-semibold text-rose-600 transition hover:border-rose-300 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-70"
                      >
                        {isResending ? "Resending..." : "Resend Verification Email"}
                      </button>
                      {resendNotice && (
                        <span className="text-xs text-rose-600">{resendNotice}</span>
                      )}
                    </div>
                  )}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-2xl bg-[var(--brand-orange)] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isSubmitting ? "Signing in..." : "Log In"}
              </button>

              <p className="text-center text-sm text-slate-500">
                New here?{" "}
                <Link
                  to="/register"
                  className="font-semibold text-[var(--brand-blue-2)] hover:text-[var(--brand-blue)]"
                >
                  Create Account
                </Link>
              </p>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setContactError("");
                    setContactSuccess("");
                    setIsContactOpen(true);
                  }}
                  className="text-sm font-semibold text-[var(--brand-blue-2)] transition hover:text-[var(--brand-blue)]"
                >
                  Contact Us
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
      {isContactOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-6 py-8">
          <div className="w-full max-w-xl rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Contact Us
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  We’d love to help
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Send us your concern and we’ll get back to you.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsContactOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-500">Name</label>
                <input
                  type="text"
                  value={contactForm.name}
                  onChange={(event) =>
                    setContactForm((prev) => ({ ...prev, name: event.target.value }))
                  }
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">Email</label>
                <input
                  type="email"
                  value={contactForm.email}
                  onChange={(event) =>
                    setContactForm((prev) => ({ ...prev, email: event.target.value }))
                  }
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">Contact Number</label>
                <input
                  type="text"
                  value={contactForm.contactNumber}
                  onChange={(event) =>
                    setContactForm((prev) => ({
                      ...prev,
                      contactNumber: event.target.value,
                    }))
                  }
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">Message</label>
                <textarea
                  value={contactForm.message}
                  onChange={(event) =>
                    setContactForm((prev) => ({ ...prev, message: event.target.value }))
                  }
                  rows={5}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
                <p className="mt-2 text-xs text-slate-400">
                  Minimum 5 words, maximum 3000 words.
                </p>
              </div>
              {contactError && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {contactError}
                </div>
              )}
              {contactSuccess && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                  {contactSuccess}
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsContactOpen(false)}
                className="rounded-2xl border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleContactSubmit}
                disabled={isContactSubmitting}
                className="rounded-2xl bg-[var(--brand-orange)] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)] disabled:opacity-60"
              >
                {isContactSubmitting ? "Sending..." : "Send Message"}
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default Login;
